import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import styled from 'styled-components';
import { navigate } from 'gatsby';
import { Helmet } from 'react-helmet';
import { GlobalStyles, colors, device } from '../styles/globals';
import Header from '../components/header';
import Footer from '../components/footer';
import { loadState, saveState } from '../utils/localStorage';
import { getHost } from '../utils/index';

import favicon from '../favicon.ico';
import faviconSVG from '../favicon.svg';
import appleicon from '../images/apple-touch-icon.png';
import { isBrowser } from '../services/msal';

const PageWrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Main = styled.main`
  position: relative;
  flex: 1;
  background-color: ${colors.grey5};
  padding-top: 120px;

  @media ${device.tablet} {
    padding-top: 90px;
  }

  @media ${device.mobileL} {
    padding-top: 60px;
  }
`;

const DefaultLayout = ({ children, noLogin }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const clinicalFAQ = loadState('clinicalFAQ');
  const patientFAQ = loadState('patientFAQ');
  const isInitialized = inProgress !== InteractionStatus.Startup && inProgress !== InteractionStatus.HandleRedirect;

  useEffect(() => {
    if (!isBrowser() || !isInitialized || !isAuthenticated) {
      return;
    }

    const fetchAppConfig = async () => {
      try {
        const host = getHost();
        const tenantId = localStorage.getItem('tenantId');
        const response = await fetch(
          `https://radius-${host}.all.health/configurator/api/v1/config?tenant_id=${tenantId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        const tenantConfigs = await response.json();
        const tenantConfigUrl = tenantConfigs?.find((config) => config.includes('web.json')
        );
        const tenantConfigResponse = await fetch(
          `https://radius-${host}.all.health/configurator/api/v1/config/get?file_name=${tenantConfigUrl}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        const tenantConfig = await tenantConfigResponse.json();
        saveState('clinicalFAQ', tenantConfig?.hosts?.clinicalFAQ);
        saveState('patientFAQ', tenantConfig?.hosts?.patientFAQ);
        window.dispatchEvent(new Event('storage'));
      } catch (e) {
        console.error('Error fetching configuration:', e);
      }
    };
    if (!(clinicalFAQ && patientFAQ)) {
      fetchAppConfig();
    }
  }, [isAuthenticated, isInitialized, clinicalFAQ, patientFAQ]);

  if (isBrowser() && !isInitialized && !noLogin) {
    return null;
  }

  if (isBrowser() && !isAuthenticated && !noLogin) {
    const selectedRealm = localStorage.getItem('realm');
    const loginHint = localStorage.getItem('loginHint');
    if (selectedRealm) {
      instance.loginRedirect({ loginHint, scopes: ['openid'] });
    } else if (isBrowser()) {
      navigate('/sign');
    }
    return null;
  }

  return (isAuthenticated || noLogin) ? (
    <>
      <GlobalStyles />
      <Helmet>
        <title>all.health</title>
        <meta name="description" content="We enable the most advanced, continuous healthcare for all people - regardless of where they live, their income, or their health status." />
        <link rel="canonical" href="https://all.health/" />
        <link rel="icon" href={favicon} sizes="any" />
        <link rel="icon" href={faviconSVG} type="image/svg+xml" />
        <link rel="apple-touch-icon" href={appleicon} />
        <link rel="dns-prefetch" href="https://cloud.typography.com/" />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cloud.typography.com/7512656/7962592/css/fonts.css"
        />
      </Helmet>
      <PageWrapper>
        <Header />
        <Main>{children}</Main>
        <Footer />
      </PageWrapper>
    </>
  ) : (
    <>
      <GlobalStyles />
      <Helmet>
        <title>all.health</title>
        <meta name="description" content="We enable the most advanced, continuous healthcare for all people - regardless of where they live, their income, or their health status." />
        <link rel="canonical" href="https://all.health/" />
        <link rel="icon" href={favicon} sizes="any" />
        <link rel="icon" href={faviconSVG} type="image/svg+xml" />
        <link rel="apple-touch-icon" href={appleicon} />
        <link rel="dns-prefetch" href="https://cloud.typography.com/" />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cloud.typography.com/7512656/7962592/css/fonts.css"
        />
      </Helmet>
    </>
  );
};

DefaultLayout.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
  noLogin: PropTypes.bool,
};

DefaultLayout.defaultProps = {
  noLogin: false,
};

export default DefaultLayout;
